
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../../../components/navbar";
import Footer from "../../../../../../../components/footer";
import Button from "../../../../../../../components/Button";
import styles from "../../../../css/Transactions/TransactionOptions/Invoices/DebtorsInvoice.module.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getDebtorsInvoice } from "../../../../../../../api/frontShop/DebtorsAPI.js";
import FormInput from "../../../../../../../components/FormInput";
import Pagination from "../../../../../../../components/Pagination";

function formatCurrency(amount) {
  const n = Number(amount || 0);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function titleCase(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function DebtorsInvoice() {
  const location = useLocation();
  const navigate = useNavigate();
  const transaction = (() => {
    const fromState = location.state?.transaction || null;
    if (fromState) {
      try {
        sessionStorage.setItem("debtors_selected_transaction", JSON.stringify(fromState));
      } catch {}
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem("debtors_selected_transaction");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [month, setMonth] = useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [patient, setPatient] = useState({ firstname: "", surname: "" });
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [month]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const pid = transaction?.patientid;
        if (!pid) throw new Error("Missing patientid");
        const res = await getDebtorsInvoice(pid, month);
        const json = res?.data;
        if (cancelled) return;
        setPatient({
          firstname: json?.data?.patient?.firstname || "",
          surname: json?.data?.patient?.surname || "",
        });
        setItems(Array.isArray(json?.data?.items) ? json.data.items : []);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
        if (!cancelled) {
          setPatient({ firstname: "", surname: "" });
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [transaction?.patientid, month]);

  const invoice = useMemo(() => {
    const subtotal = items.reduce((sum, it) => sum + Number(it.qty || 0) * Number(it.price || 0), 0);
    const vat = subtotal * 0.15;
    const total = subtotal + vat;
    const dateLabel = (() => {
      const m = String(month || "").match(/^(\d{4})-(\d{2})$/);
      if (!m) return "";
      const year = Number(m[1]);
      const mm = Number(m[2]);
      const d = new Date(Date.UTC(year, mm - 1, 1));
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    })();
    const customerName = `${patient.firstname} ${patient.surname}`.replace(/\s+/g, " ").trim();
    return {
      pharmacyName: "Durban North Pharmacy",
      dateLabel,
      customerName,
      accountType: "Credit",
      items,
      subtotal,
      vat,
      total,
      payment: "Credit",
      totalDue: total,
      invoiceNo: String(transaction?.profilenumber || transaction?.patientid || ""),
    };
  }, [items, month, patient.firstname, patient.surname, transaction?.patientid, transaction?.profilenumber]);

  const totalPages = Math.max(1, Math.ceil((invoice.items?.length || 0) / itemsPerPage));
  const pageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return (invoice.items || []).slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, invoice.items]);

  function downloadPdf() {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(14);
    doc.text("MediIntel", pageWidth / 2, 48, { align: "center" });

    doc.setFontSize(12);
    doc.text(invoice.pharmacyName, 40, 86);
    doc.setFontSize(10);
    doc.text(`Invoice ${invoice.invoiceNo ? `(${invoice.invoiceNo})` : ""}`.trim(), 40, 104);
    doc.text(`Date: ${invoice.dateLabel}`, pageWidth - 40, 104, { align: "right" });

    doc.setFontSize(10);
    doc.text("To:", 40, 132);
    doc.setFontSize(10);
    doc.text(invoice.customerName || "-", 40, 148);
    doc.text(`Account Type: ${invoice.accountType}`, 40, 164);

    autoTable(doc, {
      startY: 190,
      head: [["Date", "Item Name", "Qty", "Price", "Total"]],
      body: invoice.items.map((it) => {
        const rowTotal = Number(it.qty || 0) * Number(it.price || 0);
        return [
          String(it.date || ""),
          String(it.name || ""),
          String(it.qty || ""),
          formatCurrency(it.price),
          formatCurrency(rowTotal),
        ];
      }),
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [228, 214, 246], textColor: [17, 24, 39], fontStyle: "bold" },
      theme: "grid",
    });

    const endY = doc.lastAutoTable?.finalY || 190;
    const boxX = pageWidth - 220;
    const boxY = endY + 55;
    doc.setDrawColor(228, 214, 246);
    doc.roundedRect(boxX, boxY, 180, 88, 8, 8);

    doc.setFontSize(10);
    doc.text("SUMMARY", boxX + 12, boxY + 18);
    doc.setFontSize(9);
    doc.text("Subtotal", boxX + 12, boxY + 40);
    doc.text(formatCurrency(invoice.subtotal), boxX + 168, boxY + 40, { align: "right" });
    doc.text("VAT (15%)", boxX + 12, boxY + 56);
    doc.text(formatCurrency(invoice.vat), boxX + 168, boxY + 56, { align: "right" });

    doc.setFontSize(10);
    doc.text("Total", boxX + 12, boxY + 76);
    doc.text(formatCurrency(invoice.total), boxX + 168, boxY + 76, { align: "right" });

    doc.save(`invoice-${invoice.invoiceNo || "debtors"}.pdf`);
  }

  function proceedToPayment() {
    try {
      sessionStorage.setItem("debtors_total", String(invoice.totalDue || 0));
    } catch {}
    try {
      if (transaction?.patientid) sessionStorage.setItem("debtors_patientid", String(transaction.patientid));
    } catch {}
    navigate("/frontshop/debtors/payment-types", {
      state: {
        total: invoice.totalDue,
        fromDebtors: true,
        transaction,
      },
    });
  }

  return (
    <div className={styles.wrapper}>
      <Navbar backTo="/frontshop/debtors/transactions/options" />

      <main className={styles.main}>
        <div className={styles.topRow}>
          <div className={styles.monthSelect} aria-label="Select month">
            <span style={{ marginRight: 16, fontWeight: 600, color: "#6b7280", fontSize: 14 }}></span>
            <FormInput
              label=""
              type="month"
              name="invoiceMonth"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={styles.monthInput}
            />
          </div>
        </div>

        <section className={styles.invoiceCard}>
          <div className={styles.band}>MediIntel</div>

          <div className={styles.headerRow}>
            <div>
              <div className={styles.pharmacyName}>{invoice.pharmacyName}</div>
              <div className={styles.invoiceLabel}>Invoice</div>
            </div>
            <div className={styles.headerRight}>Date: {invoice.dateLabel}</div>
          </div>

          <div className={styles.toBox}>
            <div className={styles.toLabel}>To:</div>
            <div className={styles.toName}>{invoice.customerName || ""}</div>
            <div className={styles.toAccount}>Account Type: {invoice.accountType}</div>
          </div>

          <div className={styles.contentGrid}>
            <div className={styles.leftColumn}>
              <div className={styles.tableWrap}>
                <div className={styles.tableHeader}>
                  <div>Date</div>
                  <div>Item Name</div>
                  <div className={styles.cellRight}>Qty</div>
                  <div className={styles.cellRight}>Price</div>
                  <div className={styles.cellRight}>Total</div>
                </div>
                <div className={styles.tableBody}>
                  {error ? <div className={styles.tableRow} style={{ gridColumn: "1 / -1" }}>{error}</div> : null}
                  {loading ? <div className={styles.tableRow} style={{ gridColumn: "1 / -1" }}>Loading...</div> : null}
                  {!loading && !error && invoice.items.length === 0 ? (
                    <div className={styles.tableRow} style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                      No transactions
                    </div>
                  ) : null}
                  {pageItems.map((it, idx) => {
                    const rowTotal = Number(it.qty || 0) * Number(it.price || 0);
                    return (
                      <div key={`${it.name}-${idx}`} className={styles.tableRow}>
                        <div>{it.date}</div>
                        <div>{it.name}</div>
                        <div className={styles.cellRight}>{it.qty}</div>
                        <div className={styles.cellRight}>{formatCurrency(it.price)}</div>
                        <div className={styles.cellRight}>{formatCurrency(rowTotal)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.paginationRow}>
                <Pagination
                  currentPage={Math.min(currentPage, totalPages)}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>

            <aside className={styles.summaryCard}>
              <div className={styles.summaryTitle}>SUMMARY</div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>Subtotal</div>
                <div className={styles.summaryValue}>{formatCurrency(invoice.subtotal)}</div>
              </div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>VAT (15%)</div>
                <div className={styles.summaryValue}>{formatCurrency(invoice.vat)}</div>
              </div>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryRowTotal}>
                <div className={styles.summaryLabelTotal}>Total</div>
                <div className={styles.summaryValueTotal}>{formatCurrency(invoice.total)}</div>
              </div>
              <div className={styles.summaryRow}>
                <div className={styles.summaryLabel}>Payment</div>
                <div className={styles.summaryValue}>{invoice.payment}</div>
              </div>
            </aside>
          </div>

          <div className={styles.bottomRow}>
            <div className={styles.totalDue}>
              <div className={styles.totalDueLabel}>TOTAL DUE</div>
              <div className={styles.totalDueValue}>{formatCurrency(invoice.totalDue)}</div>
            </div>

            <div className={styles.actions}>
              <Button variant="primary" className={styles.downloadBtn} onClick={downloadPdf}>
                Download
              </Button>
              <Button variant="primary" className={styles.proceedBtn} onClick={proceedToPayment}>
                Proceed to payment
              </Button>
            </div>
          </div>
        </section>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
